import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { translations } from "@/lib/translations";

const I18nContext = createContext(null);
const STORAGE_KEY = "mylafayette_lang";
const SUPPORTED = ["fr", "en", "zh", "ru"];

function detectInitialLang() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[i18n] cannot read lang from localStorage:", err?.message || err);
  }
  const browser = (navigator.language || "fr").slice(0, 2).toLowerCase();
  return SUPPORTED.includes(browser) ? browser : "fr";
}

function lookup(obj, path) {
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[i18n] cannot persist lang:", err?.message || err);
    }
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED.includes(next)) setLangState(next);
  }, []);

  const t = useCallback((key, fallback) => {
    const val = lookup(translations[lang], key);
    if (val !== undefined) return val;
    const fallbackVal = lookup(translations.fr, key);
    return fallbackVal !== undefined ? fallbackVal : (fallback ?? key);
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t, supported: SUPPORTED }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
};
